import { createClient } from '@/lib/supabase/client';
import { PURPOSES, type PurposeAnswers } from '@/lib/consent';

/**
 * Counting how many people accept the banner and how many refuse.
 *
 * Neither Google Analytics nor the Meta pixel can answer this: both only exist
 * once somebody has accepted, so a refusal is invisible to them by
 * construction. The one answer most worth knowing is the one that guarantees
 * nothing is loaded to record it.
 *
 * What goes to the server is three booleans and nothing else. No identifier, no
 * session, no address, no timestamp finer than the date the database stamps
 * itself — see supabase/migrations/20260910090000_consent_stats.sql, which
 * keeps one row per day rather than one per person. Nothing is stored on the
 * visitor's device and nothing is read from it, so Article 5(3) does not apply
 * and counting needs no consent of its own.
 *
 * That restraint is the point rather than an omission. Recording anything that
 * identified a refuser would mean processing their data immediately after they
 * asked us not to, which is a worse failure than not knowing the number.
 */

/** The three booleans, in a shape that cannot carry anything else. */
export function consentCounts(answers: PurposeAnswers) {
  return {
    p_analytics: answers.analytics === true,
    p_behaviour: answers.behaviour === true,
    p_marketing: answers.marketing === true,
  };
}

/** True when every purpose was agreed to, false when every one was refused. */
export function isAcceptAll(answers: PurposeAnswers): boolean {
  return PURPOSES.every((purpose) => answers[purpose]);
}

export function isRejectAll(answers: PurposeAnswers): boolean {
  return PURPOSES.every((purpose) => !answers[purpose]);
}

/**
 * Records one answer. Never throws and never blocks: a counter that broke the
 * consent banner would be worse than no counter, and the visitor's own choice
 * has already been stored locally and acted on before this is called.
 */
export async function recordConsent(answers: PurposeAnswers): Promise<void> {
  try {
    const supabase = createClient();
    if (!supabase) return;
    await supabase.rpc('record_consent', consentCounts(answers));
  } catch {
    /* Offline, blocked, or the migration has not been run yet. The count is
       the only casualty. */
  }
}

export interface ConsentStatsRow {
  day: string;
  answers: number;
  acceptAll: number;
  rejectAll: number;
  partial: number;
  analytics: number;
  behaviour: number;
  marketing: number;
}

/** The counters, most recent day first. Readable only by a signed-in admin —
 *  the table's row level security says so, not this function. */
export async function loadConsentStats(
  days = 30
): Promise<{ rows: ConsentStatsRow[]; error: string }> {
  const supabase = createClient();
  if (!supabase) return { rows: [], error: 'Supabase is not configured.' };
  const { data, error } = await supabase
    .from('consent_stats')
    .select('*')
    .order('day', { ascending: false })
    .limit(days);
  if (error) return { rows: [], error: error.message };
  const rows = (data ?? []).map((row: Record<string, unknown>) => ({
    day: String(row.day ?? ''),
    answers: Number(row.answers ?? 0),
    acceptAll: Number(row.accept_all ?? 0),
    rejectAll: Number(row.reject_all ?? 0),
    partial: Number(row.partial ?? 0),
    analytics: Number(row.analytics ?? 0),
    behaviour: Number(row.behaviour ?? 0),
    marketing: Number(row.marketing ?? 0),
  }));
  return { rows, error: '' };
}

/** Totals across the rows, and the rate that is the whole reason for asking. */
export function summarise(rows: ConsentStatsRow[]) {
  const total = rows.reduce(
    (sum, row) => ({
      answers: sum.answers + row.answers,
      acceptAll: sum.acceptAll + row.acceptAll,
      rejectAll: sum.rejectAll + row.rejectAll,
      partial: sum.partial + row.partial,
      analytics: sum.analytics + row.analytics,
      behaviour: sum.behaviour + row.behaviour,
      marketing: sum.marketing + row.marketing,
    }),
    { answers: 0, acceptAll: 0, rejectAll: 0, partial: 0, analytics: 0, behaviour: 0, marketing: 0 }
  );
  const rate = (n: number) => (total.answers === 0 ? 0 : Math.round((n / total.answers) * 100));
  return {
    ...total,
    acceptRate: rate(total.acceptAll),
    rejectRate: rate(total.rejectAll),
    analyticsRate: rate(total.analytics),
    behaviourRate: rate(total.behaviour),
    marketingRate: rate(total.marketing),
  };
}

/** The month a day belongs to, as `YYYY-MM`. */
export function monthOf(day: string): string {
  return day.slice(0, 7);
}

/**
 * Every month present in the rows, newest first.
 *
 * Derived from the data rather than generated from a calendar: a month with no
 * answers is a month with nothing to show, and offering it in a picker only
 * invites somebody to select it and conclude the counter is broken.
 */
export function monthsIn(rows: ConsentStatsRow[]): string[] {
  return [...new Set(rows.map((row) => monthOf(row.day)))].sort().reverse();
}

/** `2026-09` as the month a reader recognises. */
export function monthLabel(month: string): string {
  const [year, index] = month.split('-');
  const names = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${names[Number(index) - 1] ?? month} ${year}`;
}

/** One summary per month, newest first — the shape the panel charts. */
export function byMonth(rows: ConsentStatsRow[]) {
  return monthsIn(rows).map((month) => ({
    month,
    label: monthLabel(month),
    days: rows.filter((row) => monthOf(row.day) === month).length,
    ...summarise(rows.filter((row) => monthOf(row.day) === month)),
  }));
}
